import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { INIT_CHART_OPTION_SMALL } from '@catalogues';
import {
  INIT_KPI_CHART_OPTION_LARGE,
  INIT_KPI_CHART_OPTION_MEDIUM,
  INIT_KPI_CHART_OPTION_SMALL
} from '@catalogues';
import { ChartSize, KpiChartId } from '@models';
import { CommonFunctionsService, StudyFilterService } from '@services';
import { EChartsOption } from 'echarts';
import { ECharts } from 'echarts/core';

@Component({
  selector: 'chart',
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss'
})
export class ChartComponent implements OnChanges, OnDestroy {
  chartInstance!: ECharts;
  @Input() filterService: any = inject(StudyFilterService); // Default to StudyFilterService
  @Input() initOption:
    | typeof INIT_CHART_OPTION_SMALL
    | typeof INIT_KPI_CHART_OPTION_SMALL
    | typeof INIT_KPI_CHART_OPTION_MEDIUM
    | typeof INIT_KPI_CHART_OPTION_LARGE
    | any;
  @Input() chartTitle = '';
  @Input() sortedData!: string[];
  @Input() chartOption!: EChartsOption | any;
  @Input() colorMap?: Record<string, string>;
  @Input() expandedView?: boolean;
  @Input() showAllKeys = true;
  @Input() field = '';
  @Input() chartBaseTitle? = '';
  @Input() kpiXAxisLabel: string | undefined = '';
  @Input() enableScroll = false;
  @Input() scrollVisibleItems?: number;
  @Input() kpiAxisKeysShownDefault: number | undefined = undefined;
  @Input() kpiChartSize: ChartSize = 'medium';
  @Input() kpiOnTrack?: number;
  @Input() kpiOnTarget?: number;
  @Input() isPercent = false;
  @Input() kpiChartIdentifier?: KpiChartId;
  @Input() includeToggleInHeightCalculation = false;
  @Input() hasXaxisAggLabel = false;
  @Output() expandRequested = new EventEmitter<void>();

  public chartKeys: string[] = [];
  public Math = Math;
  render = true;
  private destroyed = false;
  KPI_CHARTS_CAN_SHOW_LABELS: KpiChartId[] = [
    'barEnrolledByOrganization',
    'barEnrolledByCountry',
    'barActiveByCountryToTarget',
    'barEnrollmentByCountry',
    'barSubjectScreenFailRateByCountry',
    'barSubjectScreenFailRateBySite',
    'barSubjectScreenFailRateByReason',
    'barEarlyTermByCountry',
    'barEarlyTermBySite',
    'barEarlyTermByReason',
    'barCrfReviewCompletedByCountry',
    'barCrfReviewCompletedBySite',
    'barOnTimeDispatchClinicalMaterialsBySite',
    'barSdvComplianceByCountry',
    'barSdvComplianceBySite',
    'barLabKitsShippedByCountry',
    'barLabKitsShippedBySite',
    'barLabKitsShippedByKitType',
    'barLabSampleQueryRateByCountry',
    'barLabSampleQueryRateBySite',
    'barLabSampleQueryRateByQueryType',
    'barEreqUsageForLabSamplesByCountry',
    'barEreqUsageForLabSamplesBySite',
    'barEreqUsageForLabSamplesBySampleType',
    'barEreqUsageForLabSamplesByRequestType',
    'barLabKitWastageByKitType',
    'barDeviationRatePerSubjectByCountry',
    'barDeviationRatePerSubjectBySite',
    'barDeviationRatePerSubjectByReason'
  ];
  KPI_CHARTS_WITH_NUMERATOR_AND_DENOMINATOR_LABELS: KpiChartId[] = [
    'barEnrolledByCountry',
    'barEnrollmentByCountry',
    'barSubjectScreenFailRateByCountry',
    'barSubjectScreenFailRateBySite',
    'barSubjectScreenFailRateByReason',
    'barEarlyTermByCountry',
    'barEarlyTermBySite',
    'barEarlyTermByReason',
    'barCrfReviewCompletedByCountry',
    'barCrfReviewCompletedBySite',
    'barOnTimeDispatchClinicalMaterialsBySite',
    'barSdvComplianceByCountry',
    'barSdvComplianceBySite',
    'barLabSampleQueryRateByCountry',
    'barLabSampleQueryRateBySite',
    'barEreqUsageForLabSamplesByCountry',
    'barEreqUsageForLabSamplesBySite',
    'barDeviationRatePerSubjectByCountry',
    'barDeviationRatePerSubjectBySite',
    'barDeviationRatePerSubjectByReason'
  ];
  KPI_CHARTS_WITH_NUMERATOR_LABELS: KpiChartId[] = [
    'barEreqUsageForLabSamplesByCountry',
    'barEreqUsageForLabSamplesBySite'
  ];
  PIE_CHART_CATEGORY_LIMIT = 4;
  PIE_CHART_OTHER_CATEGORY_NAME = 'Other';
  PIE_KEYS_SHOULD_BE_REVERSED = [
    'pieDeviationRatePerSubjectByReason',
    'pieSubjectScreenFailRateByReason',
    'pieEarlyTermByReason'
  ];
  KPI_CHARTS_WITH_SMALL_AXIS_STEP = [
    'barEreqUsageForLabSamplesByRequestType',
    'barLabSampleQueryRateByQueryType'
  ];

  // Medium chart height calculation constants
  private readonly MEDIUM_CHART_VERY_LARGE_THRESHOLD = 100; // For datasets with 100+ items
  private readonly MEDIUM_CHART_LARGE_THRESHOLD = 50; // For datasets with 50-100 items
  private readonly MEDIUM_CHART_MEDIUM_THRESHOLD = 30; // For datasets with 30-50 items
  private readonly MEDIUM_CHART_SMALL_THRESHOLD = 5; // For datasets with 5 or fewer items

  private readonly MEDIUM_CHART_ITEM_HEIGHT_VERY_LARGE = 60; // More spacing for very large datasets
  private readonly MEDIUM_CHART_ITEM_HEIGHT_LARGE = 55; // Slightly compressed for large datasets
  private readonly MEDIUM_CHART_ITEM_HEIGHT_MEDIUM = 26; // Compact layout for medium datasets
  private readonly MEDIUM_CHART_ITEM_HEIGHT_DEFAULT = 32; // Standard spacing for smaller datasets

  private readonly MEDIUM_CHART_BASE_HEIGHT_LARGE = -20; // Negative offset for large datasets to reduce padding
  private readonly MEDIUM_CHART_BASE_HEIGHT_SMALL = 0; // No offset for small datasets
  private readonly MEDIUM_CHART_BASE_HEIGHT_DEFAULT = 100; // Standard padding for medium datasets

  private readonly MEDIUM_CHART_PARENT_MIN_VIEWPORT_WIDTH = 1200; // Lower bound for responsive interpolation
  private readonly MEDIUM_CHART_PARENT_MID_VIEWPORT_WIDTH = 1920; // Upper interpolation anchor before desktop fixed sizing
  private readonly MEDIUM_CHART_PARENT_MAX_COMPACT_VIEWPORT_HEIGHT = 900; // Compact layout upper height bound
  private readonly MEDIUM_CHART_PARENT_COMPACT_REDUCTION = 20; // Extra reduction for compact 1200x900 view
  private readonly MEDIUM_CHART_DENSE_ITEM_THRESHOLD = 10; // Item-count cutoff (dense when > 10)

  // Minimum height values: ensures charts are never too small to be usable
  private readonly MEDIUM_CHART_MIN_HEIGHT_WITH_TOGGLE = 220; // Minimum height when toggle controls are present
  private readonly MEDIUM_CHART_MIN_HEIGHT_WITHOUT_TOGGLE = 240; // Minimum height when no toggle controls

  // Grid margin values: spacing around chart content area
  private readonly KPI_CHART_BOTTOM_MARGIN = 40; // Standard bottom margin for chart grid
  private readonly KPI_CHART_BOTTOM_MARGIN_DENSE = 42; // Extra room for x-axis title on dense charts

  private cdr = inject(ChangeDetectorRef);
  private commonFunctionsService = inject(CommonFunctionsService);

  onChartInit(chart: ECharts) {
    this.chartInstance = chart;
    this.commonFunctionsService.resizeChartWhenContainerIsReady(chart, {
      shouldStop: () => this.destroyed
    });
  }

  onExpandButtonClick() {
    if (!this.destroyed) {
      this.expandRequested.emit();
    }
  }

  onChartClick(field: string, event: any) {
    if (this.destroyed) {
      return;
    }
    const isCategoryOtherAndPie =
      event?.name === this.PIE_CHART_OTHER_CATEGORY_NAME && event?.seriesType === 'pie';
    if (event?.name && field !== '' && !isCategoryOtherAndPie) {
      this.filterService.toggleFilter(field, event.name, 'chart');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changesArg: any = changes;
    if (changesArg && changesArg['initOption'] && this.chartInstance) {
      this.chartInstance.dispose();
      this.render = false;
      this.cdr.detectChanges();
      this.render = true;
    }

    this.updateChartOptions();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.chartInstance && !this.chartInstance.isDisposed()) {
      this.chartInstance.dispose();
    }
  }

  // Keep an eye on this method to try to reduce complexity
  private updateChartOptions() {
    if (this.chartOption && this.sortedData) {
      let keys = Object.keys(this.sortedData).filter((k) => k !== '');
      keys = this.getSlicedKeys(keys);
      this.chartKeys = keys;

      const seriesArr = Array.isArray(this.chartOption.series)
        ? this.chartOption.series
        : [this.chartOption.series];
      const chartType = seriesArr[0]?.type;

      if (chartType === 'bar') {
        const grid = this.chartOption.grid ? { ...this.chartOption.grid } : {};
        this.setKpiChartGrid(grid);
        const xAxis = this.chartOption.xAxis ? { ...this.chartOption.xAxis } : {};
        this.setKpiChartAxisStyle(xAxis);
        const yAxis = this.chartOption.yAxis ? { ...this.chartOption.yAxis } : {};
        this.setKpiChartAxisStyle(yAxis);
        const dataZoom = this.setChartDataZoom(keys);

        const showLabels = this.canShowKpiLabels();
        let seriesConfig = {
          label: {
            show: showLabels,
            position: 'right',
            formatter: (params: any) => {
              const data = (this.sortedData as any)[params.name];
              if (
                data &&
                typeof data === 'object' &&
                'numerator' in data &&
                'denominator' in data
              ) {
                return this.formatKpiLabel(data);
              }
              return '';
            }
          },
          ...(seriesArr[0] ?? {}),
          data: this.getDataValues(keys)
        };

        this.setYAxisTruncation(yAxis);
        this.setXAxisMax(xAxis, keys);
        this.setChartHeight(grid);
        this.setKpiAxisName(xAxis);

        this.setDataKeysForAxis(xAxis, keys);
        this.setDataKeysForAxis(yAxis, keys);

        seriesConfig = this.buildOnTrackLine(seriesConfig, this.isPercent);

        // Reduce x-axis steps for barEreqUsageForLabSamplesByRequestType in small size
        this.setXAxisLabelIntervals(xAxis);

        this.chartOption = {
          ...this.chartOption,
          grid,
          xAxis,
          yAxis,
          series: [seriesConfig],
          dataZoom: dataZoom,
          animation: !(
            this.kpiChartIdentifier &&
            (this.kpiChartSize === 'medium' || this.kpiChartSize === 'large') &&
            keys?.length > 100
          )
        };
      } else if (chartType === 'pie') {
        this.chartOption = {
          ...this.chartOption,
          title: {
            ...this.chartOption.title
          },
          series: [
            {
              ...(seriesArr[0] ?? {}),
              data: this.getDataValuesforPie(keys)
            }
          ]
        };
        if (this.kpiChartIdentifier) {
          this.chartOption.title.text =
            this.chartBaseTitle && this.kpiChartSize !== 'small' ? this.chartBaseTitle : '';
        }
      }
    }
  }

  private setXAxisLabelIntervals(xAxis: any) {
    if (
      this.kpiChartIdentifier &&
      this.KPI_CHARTS_WITH_SMALL_AXIS_STEP.includes(this.kpiChartIdentifier) &&
      this.kpiChartSize === 'small'
    ) {
      xAxis.axisLabel = {
        ...xAxis.axisLabel,
        interval: 0,
        formatter: function (value: any, index: number) {
          if (index % 2 === 0) {
            return value;
          }
          return '';
        }
      };
    }
  }

  private validateAxisHasCategory(axis: any): boolean {
    if (typeof axis === 'object' && axis && 'type' in axis && (axis as any).type === 'category') {
      return true;
    }
    return false;
  }

  private setDataKeysForAxis(axis: any, keys: string[]) {
    if (this.validateAxisHasCategory(axis)) {
      (axis as any).data = keys;
    }
  }
  private formatKpiLabel(data: any) {
    if (this.kpiChartIdentifier === 'barActiveByCountryToTarget') {
      return this.formatKPILabelForbarActiveByCountryToTarget(data);
    }

    if (this.hasXaxisAggLabel) {
      const percent = Math.round((data.numerator / data.denominator) * 100);
      return `${percent}%`;
    }

    if (this.kpiChartIdentifier) {
      if (this.KPI_CHARTS_WITH_NUMERATOR_AND_DENOMINATOR_LABELS.includes(this.kpiChartIdentifier)) {
        return data.value === 0 ? '-- of --' : `${data.numerator} of ${data.denominator}`;
      } else if (this.KPI_CHARTS_WITH_NUMERATOR_LABELS.includes(this.kpiChartIdentifier)) {
        return data.value === 0 ? '--' : `${data.numerator}`;
      }
    }
    return data.value === 0 ? '--' : `${data.value}`;
  }

  private formatKPILabelForbarActiveByCountryToTarget(data: any) {
    // no need to return label for small size
    if (this.kpiChartSize === 'small') {
      return '';
    }
    return data.value === 0 ? '-- of --' : `${data.numerator} of ${data.denominator}`;
  }

  getSortedDataKeysLength(): number {
    return this.sortedData ? Object.keys(this.sortedData).length : 0;
  }

  private setKpiChartGrid(grid: any) {
    if (
      this.kpiChartIdentifier &&
      (this.kpiChartSize === 'small' || this.kpiChartSize === 'medium')
    ) {
      if (this.kpiChartSize === 'medium') {
        grid.left = '5%';
        if (this.canShowKpiLabels()) {
          grid.right = '10%';
        } else {
          grid.right = '5%';
        }
      }
      if (this.kpiChartSize === 'small') {
        grid.top = '2%';
        grid.bottom = '1%';
      }
    }
  }

  private setKpiChartAxisStyle(axis: any) {
    let fontSize = 12;
    let fontWeight = 'normal';
    let nameFontWeight = 'normal';
    if (!this.kpiChartIdentifier) {
      return;
    }
    if (this.kpiChartSize === 'large') {
      fontSize = 12;
      fontWeight = 'normal';
      nameFontWeight = 'bold';
    } else if (this.kpiChartSize === 'medium') {
      nameFontWeight = 'bold';
    }

    if (this.kpiChartSize === 'medium' || this.kpiChartSize === 'large') {
      axis.axisLabel = {
        ...axis.axisLabel,
        color: '#1B1B1D',
        fontSize: fontSize,
        fontWeight: fontWeight,
        fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
      };
      axis.nameTextStyle = {
        ...axis.nameTextStyle,
        color: '#1B1B1D',
        fontSize: fontSize,
        fontWeight: nameFontWeight,
        fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
      };
    }
  }

  private setYAxisTruncation(yAxis: any) {
    if (this.kpiChartIdentifier) {
      switch (this.kpiChartSize) {
        case 'small':
          yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 100 };
          break;
        case 'medium':
          yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 250 };
          break;
        case 'large':
          yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 650 };
          break;
      }
    }
  }

  private setChartDataZoom(keys: string[]): any[] | undefined {
    if (!this.enableScroll || !this.scrollVisibleItems || keys.length <= this.scrollVisibleItems) {
      return undefined;
    }

    const startPercentage = 100 - (this.scrollVisibleItems / keys.length) * 100;

    return [
      {
        type: 'slider',
        yAxisIndex: 0,
        start: startPercentage,
        end: 100,
        width: 8,
        right: 15,
        handleSize: 0,
        showDetail: false,
        showDataShadow: false,
        brushSelect: false,
        borderColor: 'transparent',
        fillerColor: '#aaa',
        backgroundColor: '#f0f0f0',
        zoomLock: true
      },
      {
        type: 'inside',
        yAxisIndex: 0,
        start: startPercentage,
        end: 100,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: true,
        moveOnMouseMove: false,
        throttle: 0
      }
    ];
  }

  private setXAxisMax(xAxis: any, keys: string[]) {
    if (this.isPercent) {
      if (this.chartKeys.length > 0) {
        const valuesArray = this.chartKeys.map((k) => (this.sortedData as any)[k]);
        const maxValue = Math.max(...valuesArray.map((d: any) => (d.value ? d.value : 0)));
        if (maxValue <= 100) {
          xAxis.max = 100;
        } else if (maxValue > 100) {
          xAxis.max = maxValue + 20;
        }
      } else {
        xAxis.max = 100;
      }
    } else if (this.enableScroll && this.scrollVisibleItems) {
      const allValues = keys.map((k: string) =>
        !this.kpiChartIdentifier
          ? (this.sortedData as any)[k].length
          : (this.sortedData as any)[k].value
      );
      const maxValue = Math.max(...allValues);
      xAxis.max = maxValue;
      xAxis.min = 0;
    }
  }

  private canShowKpiLabels() {
    return (
      this.kpiChartIdentifier &&
      this.KPI_CHARTS_CAN_SHOW_LABELS.includes(this.kpiChartIdentifier) &&
      this.kpiChartSize !== 'small'
    );
  }

  private buildOnTrackLine(seriesConfig: any, isPercent: boolean) {
    const markLineData = [];
    if (this.kpiOnTrack !== undefined) {
      const onTrackLabel =
        this.kpiChartSize === 'small'
          ? 'On track'
          : 'On track: ' + this.kpiOnTrack + (isPercent ? '%' : '');

      markLineData.push({
        xAxis: this.kpiOnTrack,
        lineStyle: { color: '#F35200', type: 'dashed' },
        label: { show: true, formatter: onTrackLabel, position: 'end', offset: [0, 15] }
      });
    }
    if (this.kpiOnTarget !== undefined) {
      const onTargetLabel =
        this.kpiChartSize === 'small'
          ? 'Target'
          : 'Target: ' + this.kpiOnTarget + (isPercent ? '%' : '');
      markLineData.push({
        xAxis: this.kpiOnTarget,
        lineStyle: { color: '#F35200', type: 'solid' },
        label: { show: true, formatter: onTargetLabel, position: 'end', offset: [0, 30] }
      });
    }
    if (markLineData.length) {
      seriesConfig = {
        ...seriesConfig,
        markLine: {
          symbol: 'none',
          data: markLineData
        }
      };
    }
    return seriesConfig;
  }

  private setKpiAxisName(xAxis: any) {
    if (this.kpiChartIdentifier) {
      if (
        this.kpiChartSize !== 'small' &&
        typeof xAxis === 'object' &&
        xAxis &&
        'name' in xAxis &&
        this.kpiXAxisLabel !== undefined
      ) {
        // set the xAxis name
        (xAxis as any).name = this.kpiXAxisLabel;
      } else if (this.kpiChartSize === 'small') {
        (xAxis as any).name = '';
      }
    }
  }

  private getSlicedKeys(keys: string[]) {
    if (this.enableScroll && this.scrollVisibleItems) {
      return keys;
    }

    if (!this.showAllKeys) {
      if (this.kpiChartIdentifier && this.kpiAxisKeysShownDefault !== undefined) {
        keys = keys.slice(-this.kpiAxisKeysShownDefault);
      } else {
        keys = keys.slice(-4);
      }
    }
    return keys;
  }

  private getDataValues(keys: string[]) {
    return !this.kpiChartIdentifier
      ? keys.map((k: any) => (this.sortedData as any)[k].length)
      : keys.map((k: any) => (this.sortedData as any)[k].value);
  }

  private getDataValuesforPie(keys: string[]) {
    let auxCounter = 0;
    const colorMapLength = this.colorMap ? Object.values(this.colorMap).length : 0;
    const items: any[] = [];

    // invert keys to show the highest values first for specific pie charts
    if (
      this.kpiChartIdentifier &&
      this.PIE_KEYS_SHOULD_BE_REVERSED.includes(this.kpiChartIdentifier)
    ) {
      keys = keys.slice().reverse();
    }

    keys.forEach((k: any) => {
      let baseItem;
      let color = undefined;

      if (this.kpiChartIdentifier) {
        if (auxCounter < colorMapLength) {
          color = Object.values(this.colorMap!)[auxCounter];
        }
      } else {
        color = this.colorMap?.[k];
      }

      if (auxCounter > this.PIE_CHART_CATEGORY_LIMIT && this.kpiChartIdentifier) {
        if (items.find((item) => item.name === this.PIE_CHART_OTHER_CATEGORY_NAME)) {
          const othersIndex = items.findIndex(
            (item) => item.name === this.PIE_CHART_OTHER_CATEGORY_NAME
          );
          items[othersIndex].value += (this.sortedData as any)[k].value;
          return;
        } else {
          baseItem = {
            name: this.PIE_CHART_OTHER_CATEGORY_NAME,
            value: (this.sortedData as any)[k].value
          };
        }
      } else {
        baseItem = {
          name: k,
          value: this.kpiChartIdentifier
            ? (this.sortedData as any)[k].value
            : (this.sortedData as any)[k].length
        };
      }
      items.push(color ? { ...baseItem, itemStyle: { color } } : baseItem);
      auxCounter += 1;
    });
    return items;
  }

  private setChartHeight(grid: any) {
    if (
      !this.kpiChartIdentifier ||
      (this.kpiChartSize !== 'medium' && this.kpiChartSize !== 'large')
    ) {
      return;
    }

    if (this.kpiChartSize === 'medium') {
      this.configureMediumChartHeight(grid);
    } else if (this.kpiChartSize === 'large') {
      this.configureLargeChartHeight(grid);
    }
  }

  private configureMediumChartHeight(grid: any) {
    this.initOption = { ...INIT_KPI_CHART_OPTION_MEDIUM };

    const itemHeight = this.getItemHeightForMediumChart();
    const baseHeight = this.getBaseHeightForMediumChart();
    const chartSizeWithKeysLength = this.chartKeys.length * itemHeight + baseHeight;
    const minHeight = this.getMinHeightForMediumChart();

    if (this.showAllKeys && this.isDenseMediumChart()) {
      this.initOption.height = this.getAdjustedDenseHeightForMediumChart(
        chartSizeWithKeysLength,
        minHeight
      );
    } else {
      delete this.initOption.height;
    }

    grid.top = this.getTopMarginForMediumChart();
    grid.bottom = this.getBottomMarginForMediumChart();
  }

  private configureLargeChartHeight(grid: any) {
    this.initOption = { ...INIT_KPI_CHART_OPTION_LARGE };

    if (this.chartKeys.length === 1) {
      grid.top = '2%';
      grid.bottom = this.KPI_CHART_BOTTOM_MARGIN;
    } else {
      // Don't set height property at all for automatic sizing
      grid.top = this.getTopMarginForLargeChart();
      grid.bottom = this.getBottomMarginForLargeChart();
    }
  }

  private getItemHeightForMediumChart(): number {
    const keyCount = this.chartKeys.length;

    if (keyCount > this.MEDIUM_CHART_VERY_LARGE_THRESHOLD) {
      return this.MEDIUM_CHART_ITEM_HEIGHT_VERY_LARGE;
    }
    if (keyCount > this.MEDIUM_CHART_LARGE_THRESHOLD) {
      return this.MEDIUM_CHART_ITEM_HEIGHT_LARGE;
    }
    if (keyCount > this.MEDIUM_CHART_MEDIUM_THRESHOLD) {
      return this.MEDIUM_CHART_ITEM_HEIGHT_MEDIUM;
    }
    return this.MEDIUM_CHART_ITEM_HEIGHT_DEFAULT;
  }

  private getBaseHeightForMediumChart(): number {
    const keyCount = this.chartKeys.length;

    if (keyCount > this.MEDIUM_CHART_LARGE_THRESHOLD) {
      return this.MEDIUM_CHART_BASE_HEIGHT_LARGE;
    }
    if (keyCount <= this.MEDIUM_CHART_SMALL_THRESHOLD) {
      return this.MEDIUM_CHART_BASE_HEIGHT_SMALL;
    }
    return this.MEDIUM_CHART_BASE_HEIGHT_DEFAULT;
  }

  private getMinHeightForMediumChart(): number {
    return this.includeToggleInHeightCalculation
      ? this.MEDIUM_CHART_MIN_HEIGHT_WITH_TOGGLE
      : this.MEDIUM_CHART_MIN_HEIGHT_WITHOUT_TOGGLE;
  }

  private getTopMarginForMediumChart(): number | string {
    const keyCount = this.chartKeys.length;

    if (keyCount > 50) return 1;
    if (keyCount > 30) return 5;
    return '1%';
  }

  private getBottomMarginForMediumChart(): number {
    if (this.isDenseMediumChart()) {
      return this.KPI_CHART_BOTTOM_MARGIN_DENSE;
    }
    return this.KPI_CHART_BOTTOM_MARGIN;
  }

  private getTopMarginForLargeChart(): number | string {
    return this.chartKeys.length > 50 ? 1 : '2%';
  }

  private getBottomMarginForLargeChart(): number | string {
    return this.chartKeys.length > 50 ? 40 : '5%';
  }

  getChartContainerHeight(): string | null {
    if (this.kpiChartSize === 'medium' && this.kpiChartIdentifier && this.showAllKeys) {
      // Medium charts inherit height from parent container chain.
      // For dense datasets (>10), initOption.height controls internal scroll area.
      return null;
    }

    if (this.kpiChartSize === 'large' && this.chartKeys.length > 30) {
      const compactReduction = this.getCompactViewportReduction();
      return `${Math.max(500, this.chartKeys.length * 30 - compactReduction)}px`;
    }

    return null;
  }

  private isDenseMediumChart(): boolean {
    return this.chartKeys.length > this.MEDIUM_CHART_DENSE_ITEM_THRESHOLD;
  }

  private getAdjustedDenseHeightForMediumChart(
    chartSizeWithKeysLength: number,
    minHeight: number
  ): number {
    return Math.max(minHeight, chartSizeWithKeysLength);
  }

  private getViewportWidth(): number {
    if (typeof window === 'undefined' || !window.innerWidth) {
      return this.MEDIUM_CHART_PARENT_MID_VIEWPORT_WIDTH;
    }
    return window.innerWidth;
  }

  private getViewportHeight(): number {
    if (typeof window === 'undefined' || !window.innerHeight) {
      return this.MEDIUM_CHART_PARENT_MAX_COMPACT_VIEWPORT_HEIGHT;
    }
    return window.innerHeight;
  }

  private getCompactViewportReduction(): number {
    const viewportWidth = this.getViewportWidth();
    const viewportHeight = this.getViewportHeight();

    return viewportWidth <= this.MEDIUM_CHART_PARENT_MIN_VIEWPORT_WIDTH &&
      viewportHeight <= this.MEDIUM_CHART_PARENT_MAX_COMPACT_VIEWPORT_HEIGHT
      ? this.MEDIUM_CHART_PARENT_COMPACT_REDUCTION
      : 0;
  }
}
